import { Fragment, useCallback, useEffect, useState } from "react"
import {
    getPlaceholderImage,
    getRandomPlaceholderImage,
    onePhoto,
    otherPhotos,
} from "./data/photo-data"
import Modal from "react-modal"
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react"
import { MapInteractionCSS } from "react-map-interaction"
import { Carousel } from "react-responsive-carousel"
import "react-responsive-carousel/lib/styles/carousel.min.css"
import { IconButton } from "./IconButton"
import {
    FIT,
    MAX_SCALE,
    ZOOM_STEP,
    clamp,
    keepInFrame,
    looseBounds,
    zoomAbout,
} from "./zoom"
import { Image } from "../Common/Image"
import {
    HERO_SIZES,
    HERO_WIDTHS,
    METADATA_WIDTH,
    THUMBNAIL_WIDTHS,
    VIEWER_SIZES,
} from "../../utils/imageUrl"

const SLIDE_CHROME = 150

const SLIDE_HEIGHT = `calc(100vh - ${SLIDE_CHROME}px)`

const DESKTOP_THUMB_WIDTH = 110

/**
 * Tailwind's `lg` screen, the breakpoint at which this `hidden lg:grid` grid
 * becomes visible. Must track `screens.lg` in tailwind.config.js.
 */
const DESKTOP_GRID_MEDIA = "(min-width: 976px)"

const noop = () => {}

const stopPropagation = (event) => event.stopPropagation()

export const PhotoGrid = ({ item }) => {
    const photo1 = onePhoto(item)
    const photoOthers = otherPhotos(item)
    const [showCarouselModal, setShowCarouselModal] = useState(false)
    const [clickedImageIndex, setClickedImageIndex] = useState(0)
    const [carouselKey, setCarouselKey] = useState(0)
    const [zoom, setZoom] = useState(FIT)
    const [frame, setFrame] = useState({ width: 0, height: 0 })

    const images = item.gun?.photo_array || []
    const hasPhotos = images.length > 0

    // A listing with no servable photo renders its placeholder through Image's
    // fallback — seeded by the listing id so the server and client renders
    // agree — with no thumbnails and nothing to open.
    const placeholder = getPlaceholderImage(item.id)

    const isZoomed = zoom.scale > 1

    const showPhoto = useCallback((index) => {
        setClickedImageIndex(index)
        setZoom(FIT)
    }, [])

    const openCarouselAt = useCallback(
        (index) => {
            showPhoto(index)
            setCarouselKey((prevKey) => prevKey + 1)
            setShowCarouselModal(true)
        },
        [showPhoto]
    )

    const closeCarousel = useCallback(() => setShowCarouselModal(false), [])

    useEffect(() => {
        if (!showCarouselModal) {
            return
        }

        const measure = () =>
            setFrame({
                width: window.innerWidth,
                height: window.innerHeight - SLIDE_CHROME,
            })

        measure()
        window.addEventListener("resize", measure)

        return () => window.removeEventListener("resize", measure)
    }, [showCarouselModal])

    /** Step in or out about the middle of what is currently on screen. */
    const stepZoom = (direction) =>
        setZoom((current) => {
            const next = clamp(
                1,
                current.scale + direction * ZOOM_STEP,
                MAX_SCALE
            )
            const centre = { x: frame.width / 2, y: frame.height / 2 }

            return keepInFrame(zoomAbout(next, current, centre), frame)
        })

    /** MapInteraction's wheel and drag, made valid the same way as the buttons. */
    const handleZoomChange = (next) => setZoom(keepInFrame(next, frame))

    const focusCarousel = useCallback(() => {
        document.querySelector(".photo-viewer-portal .carousel-root")?.focus()
    }, [])

    const [featuredImages] = useState(images.slice(1, 9))
    const [activeImages] = useState(images)

    const heroSrc = hasPhotos ? images[0] : null

    // Photos 2-9 mount on the client only, so the primary photo is the one
    // full-size gun image this grid puts in the server-rendered document.
    //
    // Google gets no page-level image hint from the Product node (it is a
    // gallery and wins no rich result), so it picks a thumbnail from the page
    // itself. Shipping nine equally-plausible photos of this gun in the initial
    // HTML simply gave it nine candidates; combined with `primaryImageOfPage` on
    // the ItemPage node, one unambiguous candidate is the whole point (FAL-199).
    // The full gallery is still declared — and therefore still crawlable — via
    // `Product.image` in the JSON-LD.
    //
    // Safe to defer: this grid is `hidden lg:grid` (desktop only), the
    // thumbnails were already `loading="lazy"`, and the container is pinned to
    // min-h-[575px] so filling it in after hydration shifts no layout.
    //
    // One caveat this comment used to get wrong: the mobile side no longer
    // ships the hero alone. MobileGallery renders a four-thumb strip below it
    // (FAL-243), and Googlebot renders mobile-first, so those four are in the
    // document Google actually reads. They stay non-candidates by size rather
    // than by absence — MOBILE_THUMB_WIDTHS tops out below the hero's smallest
    // rendition, which PhotoGalleryTest asserts. That makes deferring these nine
    // full-size desktop thumbs matter more, not less.
    //
    // Mounting them on hydration alone was not enough: the whole grid is
    // display:none on a phone, yet the phone still built nine <img> elements
    // and evaluated their srcsets. They are gated on the grid's own breakpoint
    // instead, and follow it if the window is resized across it.
    const [showThumbnails, setShowThumbnails] = useState(false)

    useEffect(() => {
        const media = window.matchMedia(DESKTOP_GRID_MEDIA)
        const syncToBreakpoint = () => setShowThumbnails(media.matches)

        syncToBreakpoint()
        media.addEventListener("change", syncToBreakpoint)

        return () => media.removeEventListener("change", syncToBreakpoint)
    }, [])

    const isAdjacent = (index) => {
        const distance = Math.abs(index - clickedImageIndex)

        return Math.min(distance, images.length - distance) <= 1
    }

    return (
        <Fragment>
            <div className="hidden lg:grid grid-cols-1 lg:grid-cols-3 gap-[6px] mt-4">
                <div className="flex items-center justify-center lg:col-span-2 hover:cursor-pointer">
                    <Image
                        src={heroSrc}
                        alt={photo1}
                        className="w-[950px] h-[574px] bg-gray-300"
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                        width={950}
                        height={574}
                        widths={HERO_WIDTHS}
                        sizes={HERO_SIZES}
                        fallbackWidth={METADATA_WIDTH}
                        fallbackSrc={placeholder}
                        data-photo={hasPhotos ? "listing" : "placeholder"}
                        eager
                        priority="high"
                        onClick={hasPhotos ? () => openCarouselAt(0) : undefined}
                        onError={(e) => {
                            if (
                                e.target.src.includes(
                                    "/images/static/gun-skele"
                                )
                            ) {
                                return
                            }

                            e.target.srcset = ""
                            e.target.src = getRandomPlaceholderImage()
                        }}
                    />
                </div>

                <div
                    className="grid max-h-[575px] min-h-[575px] overflow-auto grid-cols-2 gap-1"
                    style={{ height: "10px" }}
                >
                    {showThumbnails &&
                        featuredImages.map((image, index) => (
                            <div
                                key={index}
                                className="relative hover:cursor-pointer"
                                onClick={() => openCarouselAt(index + 1)}
                            >
                                <Image
                                    src={image}
                                    alt={
                                        photoOthers + " " + parseInt(index + 2)
                                    }
                                    className="object-cover w-auto h-full bg-gray-300"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                    }}
                                    width={220}
                                    height={146}
                                    widths={THUMBNAIL_WIDTHS}
                                    sizes="(min-width:1024px) 220px, 33vw"
                                    fallbackSrc={placeholder}
                                />
                                {index === featuredImages.length - 1 && (
                                    <div
                                        className="absolute inset-0 flex items-center justify-center"
                                        style={{
                                            background: "rgba(55, 52, 52, .6)",
                                        }}
                                    >
                                        <span className="font-semibold text-2xl leading-[29.05px] text-neutral-50">
                                            All Photos ({activeImages.length})
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            </div>

            <Modal
                closeTimeoutMS={300}
                isOpen={showCarouselModal}
                onRequestClose={closeCarousel}
                contentLabel="Listing photos"
                portalClassName="photo-viewer-portal"
                onAfterOpen={focusCarousel}
                style={{
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.9)",
                        zIndex: 100,
                    },

                    content: {
                        inset: 0,
                        padding: 0,
                        overflow: "hidden",
                        backgroundColor: "rgba(0,0,0,1)",
                        border: "none",
                        borderRadius: 0,
                    },
                }}
            >
                <p className="absolute z-10 text-sm font-medium text-white top-5 left-5 [text-shadow:0_1px_3px_rgb(0_0_0/0.95),0_0_8px_rgb(0_0_0/0.7)]">
                    {clickedImageIndex + 1} of {images.length}
                </p>

                <div className="absolute z-10 flex gap-1.5 top-3 right-3">
                    <IconButton
                        label="Zoom out"
                        disabled={!isZoomed}
                        onClick={() => stepZoom(-1)}
                    >
                        <ZoomOut className="w-5 h-5" />
                    </IconButton>

                    <IconButton
                        label="Zoom in"
                        disabled={zoom.scale >= MAX_SCALE}
                        onClick={() => stepZoom(1)}
                    >
                        <ZoomIn className="w-5 h-5" />
                    </IconButton>

                    <IconButton label="Close photos" onClick={closeCarousel}>
                        <X className="w-6 h-6" />
                    </IconButton>
                </div>

                <Carousel
                    key={carouselKey}
                    selectedItem={clickedImageIndex}
                    onChange={showPhoto}
                    showIndicators={false}
                    showStatus={false}
                    showThumbs
                    thumbWidth={DESKTOP_THUMB_WIDTH}
                    infiniteLoop
                    useKeyboardArrows
                    swipeable
                    dynamicHeight={false}
                    renderArrowPrev={(onClickHandler, hasPrev, label) =>
                        hasPrev && (
                            <button
                                type="button"
                                onClick={onClickHandler}
                                title={label}
                                aria-label={label}
                                className="absolute z-10 flex items-center justify-center text-white -translate-y-1/2 rounded-full shadow-lg left-4 top-1/2 h-14 w-14 bg-black/70 shadow-black/40 ring-1 ring-white/25 hover:bg-black/90"
                            >
                                <ChevronLeft className="w-9 h-9" />
                            </button>
                        )
                    }
                    renderArrowNext={(onClickHandler, hasNext, label) =>
                        hasNext && (
                            <button
                                type="button"
                                onClick={onClickHandler}
                                title={label}
                                aria-label={label}
                                className="absolute z-10 flex items-center justify-center text-white -translate-y-1/2 rounded-full shadow-lg right-4 top-1/2 h-14 w-14 bg-black/70 shadow-black/40 ring-1 ring-white/25 hover:bg-black/90"
                            >
                                <ChevronRight className="w-9 h-9" />
                            </button>
                        )
                    }
                    renderThumbs={() =>
                        images.map((image, index) => (
                            <Image
                                key={index}
                                src={image}
                                alt={
                                    index === 0
                                        ? photo1
                                        : photoOthers +
                                          " " +
                                          parseInt(index + 1)
                                }
                                className="object-cover h-16 bg-neutral-800"
                                width={DESKTOP_THUMB_WIDTH}
                                height={64}
                                widths={THUMBNAIL_WIDTHS}
                                sizes={`${DESKTOP_THUMB_WIDTH}px`}
                                fallbackWidth={THUMBNAIL_WIDTHS[0]}
                                fallbackSrc={placeholder}
                            />
                        ))
                    }
                >
                    {images.map((image, index) => (
                        <div
                            key={index}
                            style={{ height: SLIDE_HEIGHT }}
                            className="w-full"
                            // Only touch needs guarding: `emulateTouch` is off,
                            // so the carousel never listens for mouse drags and
                            // a zoomed photo already pans with the mouse.
                            onTouchStart={
                                isZoomed ? stopPropagation : undefined
                            }
                        >
                            <MapInteractionCSS
                                value={index === clickedImageIndex ? zoom : FIT}
                                onChange={
                                    index === clickedImageIndex
                                        ? handleZoomChange
                                        : noop
                                }
                                minScale={1}
                                maxScale={MAX_SCALE}
                                disableZoom={index !== clickedImageIndex}
                                disablePan={
                                    index !== clickedImageIndex || !isZoomed
                                }
                                translationBounds={looseBounds(frame)}
                            >
                                <Image
                                    src={image}
                                    alt={
                                        index === 0
                                            ? photo1
                                            : photoOthers +
                                              " " +
                                              parseInt(index + 1)
                                    }
                                    className="object-contain"
                                    style={{
                                        height: SLIDE_HEIGHT,
                                        width: "100vw",
                                    }}
                                    widths={HERO_WIDTHS}
                                    sizes={VIEWER_SIZES}
                                    eager={isAdjacent(index)}
                                    fallbackSrc={placeholder}
                                />
                            </MapInteractionCSS>
                        </div>
                    ))}
                </Carousel>
            </Modal>
        </Fragment>
    )
}
